// AI refusal notice (`arb-`). The band that says the shared AI budget is spent and when it comes back.
//
// Uses the app's own warning language — the gold accent triple that InfoWithText's warning tone uses —
// and deliberately NOT the impersonation band's saturated amber-to-rose gradient. That gradient states a
// session mode nobody may dismiss; this is a dismissible, temporary budget notice, and borrowing the
// louder surface would teach learners to ignore both.
//
// The `arb-offset` class goes on <html> while the band is up, mirroring `imb-offset`. The two COMPOSE:
// with both bands showing the reserved space is 88px, and this band sits under the impersonation one,
// because a support session outranks a quota notice.
export const aiRefusalNoticeStyles = `
:root.arb-offset {
  scroll-padding-top: 44px;
}
:root.arb-offset body {
  padding-top: 44px;
}
:root.imb-offset.arb-offset body {
  padding-top: 88px;
}

.arb-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Above BaseModal's overlay (9999) so a refused action is explained even on a modal screen, and below
     the impersonation band and the portaled input popovers, which must still win. */
  z-index: 99998;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: 44px;
  padding: var(--space-2) var(--space-4);
  box-sizing: border-box;
  background: var(--accent-gold-bg);
  border-bottom: 1px solid var(--accent-gold-border);
  color: var(--accent-gold-text);
  font-family: var(--font-sans);
  /* Opaque enough to read over the learner background without a backdrop filter, which on a fixed
     full-width band is a per-frame cost for no visual gain here. */
  box-shadow: 0 2px 10px rgba(146, 64, 14, 0.1);
}
:root.imb-offset .arb-bar {
  top: 44px;
}

.arb-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.arb-text {
  flex: 1 1 auto;
  /* Load-bearing: without it the flex item refuses to shrink and the row overflows the viewport
     instead of ellipsising. */
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-2);
}

.arb-title {
  font-size: var(--fs-banner-body);
  font-weight: 700;
}

.arb-detail {
  font-size: var(--fs-banner-body);
  font-weight: 500;
  line-height: 1.5;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arb-reset {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--fs-banner-label);
  font-weight: 700;
  padding: 2px var(--space-2);
  border-radius: var(--rad-catchup-full);
  background: var(--catchup-white);
  border: 1px solid var(--accent-gold-border);
  white-space: nowrap;
}

.arb-dismiss {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--rad-catchup-full);
  background: transparent;
  color: var(--accent-gold-text);
  cursor: pointer;
}

.arb-dismiss:hover {
  background: var(--catchup-white);
  border-color: var(--accent-gold-border);
}

.arb-dismiss:focus-visible {
  outline: 2px solid var(--accent-gold-border);
  outline-offset: 2px;
}

/* Below this width the explanatory half gives way first: that the budget is full and WHEN it frees
   matter more than the sentence saying the pool is shared. Same trade the impersonation band makes. */
@media (max-width: 560px) {
  .arb-detail {
    display: none;
  }
}
`;
